'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, ControlledInput, Entering, Typography } from 'components';

import { NEW_PASSWORD_REQUIRED, SMS_MFA } from 'constants/challengeNames';
import { DARK_MODE_PERSISTED_STATE } from 'constants/persistedStates';
import { handlerEnter } from 'handlers/handlerEnter';
import { toaster } from 'helpers/toaster';
import useAuth from 'hooks/useAuth';
import useLocaleAuth from 'hooks/useLocaleAuth';
import usePersistentContext from 'hooks/usePersistenceState';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { decrypt } from 'utils/crypting';
import { isLocalAuthEnabled } from 'utils/localAuth';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { useAuthenticationContext } from '../_context/AuthenticationContext';

YupPassword(yup);

interface SigninInput {
  email: string;
  password: string;
}

export default function Login() {
  const auth = useAuth();
  const router = useRouter();
  const { redirectHome } = useLocaleAuth();
  const t = useTranslations();

  const searchParams = useSearchParams();
  const [, setDarkMode] = usePersistentContext(DARK_MODE_PERSISTED_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const { setCognitoUser, setCognitoUserEmail, setCognitoUserPassword } =
    useAuthenticationContext();

  const emailDefault = decodeURIComponent(searchParams.get(KeysEnum.Email) ?? '');
  const passwordDefault = decrypt(searchParams.get(KeysEnum.Password) ?? '');

  const redirectToDefault = searchParams.get(KeysEnum.RedirectTo);

  // don't use strong passwords for local auth
  let passwordSchema;
  if (isLocalAuthEnabled()) {
    passwordSchema = yup.string().required(t('messages.fields.password_required'));
  } else {
    passwordSchema = yup.string().password().required(t('messages.fields.password_required'));
  }

  const schema = yup.object().shape({
    email: yup
      .string()
      .nullable()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_required')),
    password: passwordSchema
  } as TScheme<SigninInput>);
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    setValue
  } = useForm<SigninInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: emailDefault,
      password: passwordDefault
    }
  });

  const autoCompleteHandler = useCallback(
    (tag: string) => {
      return rememberMe ? tag : 'off';
    },
    [rememberMe]
  );

  const email = watch('email');
  const searchParamsNew = new URLSearchParams();
  searchParamsNew.set('email', email);
  const params = email ? '?' + searchParamsNew.toString() : '';

  const handleFetch = useCallback(async () => {
    try {
      setIsLoading(true);
      const { email, password } = getValues();
      const user = await auth.signIn(email, password);

      if (user?.challengeName === NEW_PASSWORD_REQUIRED) {
        setCognitoUser(user);

        return router.push('/auth/set-password');
      }

      if (user?.challengeName === SMS_MFA) {
        setCognitoUser(user);
        setCognitoUserEmail(email);
        setCognitoUserPassword(password);

        return router.push('/auth/two-factor');
      }

      if (user?.attributes?.email_verified || user.userLocalSession?.authToken) {
        setIsLoading(false);
        setDarkMode('');

        return redirectHome(redirectToDefault);
      }

      throw user;
    } catch (error) {
      notifyException(error);
      setIsLoading(false);
      toaster.error(t('messages.error.sign_in'));
    }
  }, [
    getValues,
    auth,
    setCognitoUser,
    router,
    setCognitoUserEmail,
    setCognitoUserPassword,
    setDarkMode,
    redirectHome,
    redirectToDefault,
    t
  ]);

  const onSubmit = handleSubmit(handleFetch);

  useEffect(() => {
    setValue('email', emailDefault);
    setValue('password', passwordDefault);
  }, [emailDefault, passwordDefault, setValue]);

  return (
    <>
      <div className="flex flex-col gap-4 px-1 pt-8">
        <Entering initial={false} damping={20} mass={0.5} stiffness={120} delay={0.1}>
          <ControlledInput
            control={control}
            error={errors.email}
            label={t('auth.sign_in.label_email')}
            type="text"
            name="email"
            autoComplete={autoCompleteHandler('email')}
            classNameParent="w-full"
            defaultValue={emailDefault}
          />
        </Entering>
        <Entering initial={false} damping={20} mass={0.5} stiffness={120} delay={0.2}>
          <ControlledInput
            control={control}
            error={errors.password}
            label={t('auth.sign_in.label_password')}
            type="password"
            name="password"
            autoComplete={autoCompleteHandler('password')}
            defaultValue={passwordDefault}
            classNameParent="w-full"
          />
        </Entering>
        <Entering initial={false} damping={20} mass={0.5} stiffness={120} delay={0.3}>
          <div className="flex justify-between py-2">
            <Checkbox
              checked={rememberMe}
              onChange={setRememberMe}
              name="rememberMe"
              label={t('auth.sign_in.remember_me')}
            />
            <Link
              href={'/auth/forgot-password' + params}
              className="flex cursor-pointer items-center justify-center text-base leading-normal text-secondary-link"
            >
              <Typography variant="regular4">{t('auth.sign_in.forgot_password')}</Typography>
            </Link>
          </div>
        </Entering>
        <Entering initial={false} damping={20} mass={0.5} stiffness={120} delay={0.4}>
          <div className="flex w-full gap-4">
            <Button
              onClick={onSubmit}
              onKeyDown={handlerEnter(onSubmit)}
              label={t('auth.sign_in.button_login_in')}
              disabled={isLoading}
              className="w-full"
            />
          </div>
        </Entering>
        <Entering initial={false} damping={20} mass={0.5} stiffness={120} delay={0.5}>
          <span className="flex items-center justify-center pt-8">
            <Typography variant="light4" className="text-tertiary-200">
              {t('auth.sign_in.dont_have_account')}
            </Typography>
            <Typography variant="regular4">
              <Link
                href="/auth/sign-up"
                className="ml-1 cursor-pointer text-sm text-secondary-link"
              >
                {t('auth.sign_in.sign_up')}
              </Link>
            </Typography>
          </span>
        </Entering>
      </div>
    </>
  );
}
